/**
 * Tasks: the register, the board and one task (`KLQ-20260821-001`, C7/C8).
 *
 * The namespace fills up as the screen does; what is here is the register, its filter bar and
 * the failures.
 *
 * One copy rule carries weight from the first key, and it is §D's: **no number on this screen
 * claims to be a total.** A loaded count is written with a `+` while the cursor has more to
 * give, and drops it only when the cursor is exhausted. There is no counts endpoint, and a
 * count that quietly dropped the `+` would be a guess wearing a fact’s clothes. That is why
 * `count.tasks` and `count.tasksMore` are two messages rather than one with a suffix bolted
 * on: where the `+` sits relative to the noun is the translator’s call.
 *
 * The empty states are four sentences and a fifth, and the split is C7's: a project with no
 * tasks is addressed in the reader's own verb, and a *filter* with no matches is a different
 * fact that gets *Clear filters* rather than the same invitation.
 *
 * `error.conflict` is deliberately generic. The tasks domain answers `409` to several
 * different situations and writes a sentence for each; the screen prints the server’s sentence
 * when there is one (`problems.ts`) and falls back to this only when there is not.
 */

export const tasksEn = {
  "page.title": "Tasks",

  // §D. Two messages, not one plus a suffix: German and Slovak may want the `+` somewhere
  // else, and the `bare` pair is for a slot whose own label already says what is counted.
  "count.tasks": "{count, plural, one {# task} other {# tasks}}",
  "count.tasksMore": "{count, plural, one {# task} other {# tasks}}+",
  "count.bare": "{count, number}",
  "count.bareMore": "{count, number}+",

  // The two views of the same filter object (§C). "List", not "register", in the switch:
  // the register is this spec's word for the surface, not a word the reader needs.
  "view.legend": "View",
  "view.register": "List",
  "view.board": "Board",

  // A column's own empty sentence — its header already names the status, so this does not.
  "board.empty": "Nothing here.",

  "status.loading": "Loading…",
  "list.more": "Show older",
  "list.loadingMore": "Loading…",

  // The four built-in saved filters. Sent as `saved=` and answered by the server, which is why
  // there is no key here for "this week starts on Monday": it is not this side's question.
  "saved.legend": "Saved filters",
  "saved.mine": "Mine",
  "saved.overdue": "Overdue",
  "saved.unassigned": "Unassigned",
  "saved.thisWeek": "This week",
  "saved.needsAnOwner": "Needs an owner",
  "saved.myCompany": "My company",

  "filter.legend": "Filters",
  // The collapsed bar's trigger and its panel (`KLQ-20260910-001` §B). The number beside it
  // counts narrowed *fields*, not values — two statuses are one narrowed field — so the word
  // must stay a plain noun that a count can follow.
  "filter.open": "Filters",
  "filter.close": "Close filters",
  "filter.any": "Any",
  "filter.clear": "Clear filters",
  "filter.kind": "Type",
  "filter.kindAny": "Any type",
  "filter.status": "Status",
  "filter.statusAny": "Any status",
  "filter.priority": "Priority",
  "filter.priorityAny": "Any priority",
  "filter.assignee": "Assignee",
  "filter.assigneeAny": "Anyone",
  "filter.assigneeUnassigned": "Unassigned",
  "filter.dueFrom": "Due from",
  "filter.dueUntil": "Due until",
  "filter.attachments": "Attachments",
  "filter.attachmentsWith": "With photos or files",
  "filter.attachmentsWithout": "Without",
  "filter.drawing": "Drawing",
  "filter.drawingPinned": "Pinned to a drawing",
  "filter.drawingUnpinned": "Not pinned",
  "filter.resolved": "Closed by a version",
  "filter.resolvedYes": "A version resolved it",
  "filter.resolvedNo": "Nothing to show for it",
  // The cross on one chip of a filter set (`KLQ-20260825-003` §G). Not `assign.remove`:
  // that one names the task's own assignee picker, and one message serving two controls is
  // a translation that cannot be right for both.
  "filter.remove": "Remove {name}",

  // chat1.md §6.1: task, issue, question. Never "ticket", never "card".
  "kind.task": "Task",
  "kind.issue": "Issue",
  "kind.question": "Question",

  // §6.3, verbatim. Never invent a fifth.
  "axis.legend": "Group by",
  "axis.location": "Location",
  "axis.object": "Object",
  "axis.phase": "Phase",
  "axis.status": "State",
  "axis.priority": "Priority",
  "axis.noObject": "No object",
  "axis.noPhase": "No phase",
  "axis.noStatus": "No state",
  "axis.noPriority": "No priority",
  "status.open": "Open",
  "status.inProgress": "In progress",
  "status.done": "Done",
  "status.closed": "Closed",

  "priority.high": "High",
  "priority.normal": "Normal",
  "priority.low": "Low",

  // The tree as a filter facet (§A). It narrows and never widens, so clearing it restores the
  // project-wide register and nothing more.
  "under.label": "Location",
  "under.all": "Whole project",
  "under.active": "Showing tasks under {name}.",
  "under.showing": "Showing tasks under",
  "under.remove": "Stop showing only {name}",
  "under.hidden":
    "{count, plural, one {# task elsewhere in the project} other {# tasks elsewhere in the project}}",
  "under.clear": "Show the whole project",
  "under.unknown": "a part of the project this list cannot name",

  "label.legend": "Labels",
  "label.loading": "Loading labels…",
  "label.none": "This project has no labels yet.",
  "label.add": "Add a label",
  "label.newName": "New label",
  "label.create": "Create",
  "label.cancel": "Cancel",

  "export.action": "Export CSV",

  // Raising a task. The field labels reuse the filter bar's and the detail's — one word per
  // concept, per the terminology rule. The pin block appears only where the chosen location
  // is a drawing; a wrong guess about that is the server's `422`, rendered, not prevented.
  // --- Generating tasks (`KLQ-20260825-001`) -------------------------------------
  // The dialog raises one task per node the slicer and the filter leave standing. It names
  // no nodes: `under` plus a filter go to the server, which intersects them with the
  // caller's zone scope — which is why every sentence here talks about *what is on screen*
  // rather than about a selection.
  "generate.action": "Generate tasks",
  "generate.title": "Generate tasks",
  "generate.close": "Close",
  "generate.done": "Done",
  "generate.preview": "Preview",
  "generate.submit":
    "{count, plural, =0 {Create tasks} one {Create # task} other {Create # tasks}}",
  "generate.scopeWhole": "Over the whole project, as far as you can see it.",
  "generate.scopeUnder": "Under {name}.",
  "generate.types": "What kind of work",
  "generate.typesHint":
    "Your studio’s task types, from Studio settings. Pick more than one and each node gets one task of each.",
  "generate.noTypes":
    "Your studio has no task types yet. An admin can add them in Studio settings.",
  "generate.fromType": "From the type",
  "generate.verification":
    "Require someone to confirm the work, whatever the type says",
  "generate.claimOneType":
    "Drawings can only be given away when one task type is picked.",
  "generate.nodeKind": "Level",
  "generate.nodeKindAny": "Every level",
  "generate.onlyWithoutTask": "Only where there is no task yet",
  "generate.nameContains": "Name contains",
  "generate.claim": "Give each task the drawings of its node",
  "generate.planTitle": "What this will do",
  "generate.stale": "You changed the form. Preview again before creating.",
  "generate.nothing":
    "Nothing to generate here. Widen the slice or the filter.",
  "generate.excluded":
    "{count, plural, one {# more node is outside the current slice.} other {# more nodes are outside the current slice.}}",
  "generate.softCap":
    "That is a large batch. Read the list before you create it.",
  "generate.repeatedTitles":
    "{count, plural, one {# task shares its title with another} other {# tasks share their titles with others}}. They are told apart by where they sit. Add {code} if you want them named differently.",
  "generate.skipAlready": "{code}: already has this task",
  "generate.numbered": "#{number, number} · {code}",
  "generate.claims":
    "{claimed, plural, one {# drawing claimed} other {# drawings claimed}}, {skipped, plural, =0 {none skipped} one {# already owned} other {# already owned}}.",
  "generate.written":
    "{count, plural, one {# task created.} other {# tasks created.}}",

  "create.action": "New task",
  "create.title": "New task",
  "create.submit": "Create task",
  "create.close": "Close",
  "create.location.none": "No location",
  "create.location.drawing": "{name} (drawing)",
  "create.anchor.legend": "Pin on the drawing",
  "create.anchor.hint":
    "Where on the sheet, as a percentage across and down. Leave both empty for no pin.",
  "create.anchor.x": "Across (%)",
  "create.anchor.y": "Down (%)",
  "create.anchor.page": "Page",
  "create.verification": "I will check the work before this task closes.",
  "create.verificationByType": "(this type always does)",
  "create.typeLabel": "Type of work",
  "create.typeNone": "No type",
  "create.typeApplied":
    "Title, kind and priority came from this type. Change any of them.",
  "create.error.titleRequired": "Give the task a title.",
  "create.photo": "Photo",
  "create.photoFailed": "The photo did not attach. The task was created.",
  "create.photoRetry": "Try the photo again",

  "row.number": "#{number}",
  // A drawing's tasks (`SlotTasks.tsx`): the slot detail's coordination view and the publish
  // drawer's second step. Nothing closes without a tick, and the copy says so.
  "slot.title": "Tasks",
  "slot.open": "Still open on this drawing",
  "slot.resolved": "Resolved by its versions",
  "slot.loading": "Loading the tasks…",
  "slot.error": "The tasks on this drawing could not be loaded.",
  "slot.noneOpen": "Nothing is open on this drawing.",
  "slot.noneResolved":
    "No task has been closed by a version of this drawing yet.",
  "slot.more": "More in the task register.",
  "resolve.title": "Did this version resolve anything?",
  "resolve.body":
    "Tick the tasks this sheet actually resolves. They close with this version recorded against them. Nothing is closed unless you tick it.",
  "resolve.close":
    "{count, plural, =0 {Close tasks} one {Close # task} other {Close # tasks}}",
  "resolve.skip": "Not now",
  "resolve.done": "Done",
  "resolve.notYours":
    "{count, plural, one {# more open task here is} other {# more open tasks here are}} not yours to close.",
  "resolve.failed": "These did not close:",
  "row.unassigned": "Unassigned",
  "bulk.skip.closed": "Closed: reopen it to change it",
  "bulk.skip.transition": "Already past that state",
  "bulk.skip.forbidden": "Not yours to change",
  "bulk.skip.verification": "Waiting on the person who raised it to verify",
  "bulk.skip.gone": "No longer there",

  // The selection's own words (`KLQ-20260825-006` §G, §H). The count is a plural the
  // translator owns, never a number glued onto a sentence — and `selectedLoaded` is §D.3:
  // when a container contributed, the sentence says *loaded*, because no number on this
  // screen claims to be a total.
  "bulk.selected":
    "{count, plural, one {# task selected} other {# tasks selected}}",
  "bulk.selectedLoaded":
    "{count, plural, one {# loaded task selected} other {# loaded tasks selected}}",
  "bulk.confirm": "{what}: {count, plural, one {# task} other {# tasks}}?",
  "bulk.apply": "Apply",
  "bulk.cancel": "Cancel",
  "bulk.clear": "Clear selection",
  "bulk.more": "More actions",
  "bulk.menu": "Task actions",
  "bulk.applied":
    "{count, plural, one {# task changed} other {# tasks changed}}.",
  // Both halves, always — "3 failed" hides that nine worked, and the reason is the server's
  // own, in this person's language.
  "bulk.appliedSkipped":
    "{count, plural, one {# task changed} other {# tasks changed}}, {skipped, plural, one {# skipped} other {# skipped}}: {reason}",
  "bulk.assign": "Assign…",
  "bulk.assignTo": "Assign to {name}",
  "bulk.unassign": "Unassign…",
  "bulk.unassignFrom": "Unassign {name}",
  "bulk.priority": "Priority",
  "bulk.priorityTo": "Priority {value}",
  "bulk.addLabel": "Add label",
  "bulk.addLabelTo": "Add label {name}",
  "bulk.removeLabel": "Remove label",
  "bulk.removeLabelFrom": "Remove label {name}",
  "bulk.clearDue": "Clear due date",
  "bulk.open": "Open task",
  "row.due": "due {when}",
  "row.noDue": "no due date",
  "row.pinned": "Pinned to a drawing",

  // The three things the member list can fail to say, kept apart on purpose: names come from
  // `GET /projects/{id}/members`, which an engineer or a contractor may not read at all
  // (`queries.ts:useMemberNames`). The em dash stays reserved for somebody who left.
  "person.pending": "Loading…",
  "person.unavailable": "Name unavailable",

  // C7's persona-aware empty states. Each says what to do next in the reader's own verb.
  "empty.explains": "A task says what the project still owes, and to whom.",
  "empty.architect.title": "Capture your first task.",
  "empty.architect.body":
    "A task says what the project still owes and who owes it. Raise one from a drawing, or from here.",
  "empty.engineer.title": "Nothing is waiting on you here.",
  "empty.engineer.body":
    "Tasks and questions raised on this project will show up in this list.",
  "empty.contractor.title": "No issues assigned to you.",
  "empty.contractor.body":
    "Anything raised for you on site lands here, with the drawing it was found on.",
  "empty.client.title": "No tasks yet.",
  "empty.client.body":
    "This is where the project's open work is listed, once there is any.",
  "empty.filtered.title": "Nothing matches these filters.",
  "empty.filtered.body":
    "Widen a filter, or clear them all to see the whole register.",

  // --- The task detail (C8) ---------------------------------------------------
  // One rule carries this block: nothing here promises what the server may refuse. The
  // workflow verbs render only for the transitions the payload allows; the reopen hint and
  // the closed-record line state the model's own rule (closed is terminal, reopening is a new
  // task); and `assign.admits` is §F's forced sentence — said *before* the assignment that
  // would let a firm in, never after.
  "detail.back": "Back to tasks",
  "detail.raisedBy": "Raised by {name} · {when}",
  "detail.closedBy": "Closed by {name} · {when}",
  "detail.due": "Due date",
  "detail.location": "Location",
  "detail.noLocation": "Not tied to a place.",
  "detail.locationUnknown": "A part of the project this screen cannot name.",
  "detail.pin": "Pinned at {x} %, {y} %",
  "detail.openDrawing": "Open {name} in the viewer",
  "detail.thumbnailAlt": "Thumbnail of {name}",
  "detail.verification":
    "The person who raised this task checks the work before it closes.",
  "detail.closedRecord":
    "This task is closed. The record stands as it was decided; reopening raises a new task.",
  "detail.description": "Description",
  "detail.noDescription": "No description.",
  "detail.edit": "Edit",
  "detail.titleLabel": "Title",
  "detail.save": "Save",

  "workflow.legend": "Workflow",
  "workflow.toOpen": "Move back to open",
  "workflow.toInProgress": "Start progress",
  "workflow.toDone": "Mark done",
  "workflow.toClosed": "Close",

  "reopen.action": "Reopen",
  "reopen.hint":
    "Closed is final. Reopening raises a new task pointing back at this one; this record does not change.",
  "reopen.created": "Reopened as",
  "reopen.origin": "Reopened from",
  "reopen.originUnavailable":
    "an earlier, closed task this screen cannot show you",

  "thread.title": "Thread",
  "thread.empty": "Nothing said yet.",
  "thread.placeholder": "Say something about this task…",
  "thread.send": "Comment",
  "thread.admitted":
    "{firm} was let into this task. {count, plural, one {# earlier comment stays internal} other {# earlier comments stay internal}}.",

  "attachment.title": "Attachments",
  "attachment.none": "No photos or files yet.",
  "attachment.add": "Add a photo or file",
  "attachment.retry": "Try again",
  "attachment.unnamed": "Unnamed file",
  "attachment.uploading": "Uploading… {percent} %",
  "attachment.insecure": "Uploads need a secure (HTTPS) connection.",
  "attachment.failed": "The upload did not go through. Try again.",
  "attachment.rejected": "The file was refused: {reason}",
  "attachment.timedOut":
    "The file check is taking unusually long. The file is uploaded; try attaching it again in a moment.",

  "watchers.title": "Watchers",
  "watchers.none": "Nobody is watching this task.",
  "watchers.watch": "Watch",
  "watchers.stop": "Stop watching",

  "audience.title": "Who can see this",
  "audience.internal": "No outside firm has been let into this task.",
  "audience.firmUnknown": "A firm this screen cannot name",
  "audience.reason.createdBy": "Raised the task",
  "audience.reason.assignedTo": "Was assigned the task",
  "audience.reason.mentioned": "Mentioned in the thread",
  "audience.reason.shared": "The task was shared with them",
  "audience.since": "since {date}",

  "assign.admits":
    "Assigning to {name} lets {firm} into this task. They will read the thread from today onward; everything earlier stays internal.",
  "assign.confirm": "Assign and let them in",
  // The multi-assignee picker (`KLQ-20260824-001`): the select adds one person per pick, the
  // chip removes the one it names. Assignees are equals, so neither verb ranks anybody.
  "assign.add": "Add a person…",
  "assign.remove": "Remove {name}",
  // The firm that owes the task (`KLQ-20260820-007`) — a second answer beside the people, never
  // merged into their picker. "Off-system" is the firm with no Kalqa account: assigning it
  // notifies nobody, and the mark says so in words rather than in a colour.
  "assign.firm": "Firm",
  "assign.firmDirectory": "From the studio’s directory, joins this project",
  "assign.firmNone": "No firm",
  "assign.firmOption": "{name} (off-system)",
  "assign.firmOffSystem": "Off-system · not notified",
  "assign.firmUnknown": "A firm no longer on the roster",
  "assign.firmAdmits":
    "Assigning to {firm} lets them into this task. They will read the thread from today onward; everything earlier stays internal.",

  // The mention picker under the composer (`MCK-20260521-008`). `mention.external` is the
  // whole option label, not a suffix bolted onto a name: where the mark sits relative to the
  // person and the firm is the translator's call, and in a native `<option>` the mark has
  // nowhere else to live. `mention.grantsAccess` is §F's sentence again, worded for naming
  // somebody rather than assigning to them — said before the mention is added, never after.
  "mention.legend": "Mention",
  "mention.add": "Mention someone…",
  "mention.remove": "Stop mentioning {name}",
  "mention.external": "{name} (outside firm: {firm})",
  "mention.grantsAccess":
    "Mentioning {name} lets {firm} into this task. They will read the thread from today onward; everything earlier stays internal.",
  "mention.confirm": "Mention and let them in",

  // The Deliverables section (`KLQ-20260824-001`). Rows are named by their identifier and
  // document title — a deliverable is never called a task (§6 terminology). The version is
  // stated as presence, because the wire carries an id and an id is not a sentence.
  "deliverable.title": "Deliverables",
  "deliverable.none": "This task owns no drawings.",
  "deliverable.noVersion": "no version yet",
  "deliverable.hasVersion": "current version on file",
  "deliverable.status.planned": "Planned",
  "deliverable.status.inProgress": "In progress",
  "deliverable.status.done": "Done",

  // The subtask entrance (decision 3): a subtask is its own task, raised at this task's
  // place, with one accountable person. The hint says where it will show up, because this
  // screen does not list them — the register and the hierarchy view do.
  "subtask.title": "Subtasks",
  "subtask.add": "Add subtask",
  "subtask.hint":
    "A subtask is its own task, raised at this task's place with one accountable person. It shows up in the register like any other task.",

  "error.forbidden": "You are not allowed to do that on this task.",
  "error.gone": "That task is no longer here.",
  "error.conflict": "That is no longer possible on this task.",
  "error.validation":
    "That request was refused. Check the fields and try again.",
  "error.rateLimited": "Too many requests. Wait a moment and try again.",
  "error.cursor": "That list moved on. Reload it to see the latest.",
  "error.filter":
    "That filter is not one this project knows. Clear it and try again.",
  "error.unexpected": "Something went wrong. Try again.",

  // The hierarchy view (`KLQ-20260824-001`) — the third arrangement of the same rows.
  // "Plan" and not "hierarchy" in the toggle: the reader is looking at what is planned where,
  // and "hierarchy" is this repo's word for the tree, not theirs (§6 terminology).
  "view.hierarchy": "Plan",
  "plan.table": "Work by location, and the drawings it produces",
  "plan.column.what": "Work",
  "plan.column.who": "Who",
  "plan.column.when": "When",
  "plan.column.state": "State",
  // What an inherited name says about itself (`KLQ-20260828-003` §E): it was not chosen for
  // this row. Lives in the cell's `title`, beside a name drawn quieter than a stated one.
  "plan.responsibleFrom": "Responsible for {name}",

  // Paste-import (`KLQ-20260824-002`). The field labels are what the mapping control offers;
  // the reasons are what a refused row says. Both are the *domain's* words — the drawer around
  // them belongs to `app`.
  "import.field.title": "Work",
  "import.field.location": "Where",
  "import.field.kind": "Kind",
  "import.field.priority": "Priority",
  "import.field.due": "When",
  "import.field.assignees": "Who",
  "import.reason.noTitle": "A task needs a title.",
  "import.reason.noLocation": "No location for this row.",
  "import.reason.unknownLocation": "No such place in this project.",
  "import.reason.ambiguousLocation":
    "More than one place is called that. Use the full path.",
  "import.reason.locationRow": "Read as a location",
  "import.reason.unknownAssignee": "{name} is not on this project.",
  "import.reason.badDate": "{value} is not a date (use 2026-09-30).",
  "import.reason.badKind": "{value} is not a kind of work.",
  "import.reason.badPriority": "{value} is not a priority.",
  "import.reason.exists": "This task is already here.",
  "import.reason.refused": "The server refused this row.",
  "plan.version": "v{counter, number}",
  // Both causes in one section, deliberately: a task with no address and a task whose address
  // this reader's zones do not cover are the same fact on their screen.
  "plan.unplaced": "Unplaced",
  // The tree's own sentinel row (owner, 2026-09-10). Present tense, because by the time
  // anybody reads it the request is already in flight — it is a status, not a button.
  "plan.more": "Loading more of this level…",
  "plan.moreCount": "{count, plural, one {# more here} other {# more here}}",
  "plan.explains": "The plan shows the project’s work by place on the building, and who does it.",
  "plan.emptyTitle": "Nothing planned",
  "plan.empty": "Nothing is planned here yet.",
  // §D again, in the one place a group could be mistaken for a total: a header counts the
  // rows that are loaded, and there are more to load.
  "plan.partial":
    "Counts cover the pages loaded so far. Show older to fill them in.",
  "plan.mode.flat": "List",

  // The left rail (the shared Slicer). Its two facets are the two questions worth asking of a
  // plan: which part of the building, and whose desk.
  // The slicer's axes (`KLQ-20260820-005` §6). **Which axes exist is the server's
  // answer, not this list** — these are the names a person reads for the fields it
  // publishes today, keyed by the wire field in this app's camelCase. An axis with no
  // key here still appears, named by its own field: `useLooseMessages`'s fallback.
  //
  // `slicer.`, not `axis.` — that prefix is the plan's *group by* control
  // (`KLQ-20260904-004`), which is a different operation on the same words.
  "slicer.unknownValue": "something this list cannot name",
  "slicer.noValues": "Nothing to slice by here.",
  "slicer.under": "Location",
  "slicer.kind": "Kind",
  "slicer.status": "Status",
  "slicer.priority": "Priority",
  "slicer.assigneeUserId": "Person",
  "slicer.labelIds": "Label",
  // The task's deliverables' object, not the task's — a task has none of its own.
  "slicer.objectId": "Object",

  "slicer.title": "Axis",
  "slicer.location": "Location",
  "slicer.assignee": "Person",
  "slicer.noLocations": "This project has no levels yet.",
  "slicer.noPeople": "Nobody to slice by here.",

  // The root `/tasks` page (`MCK-20260521-009`): what this person owes, across every project.
  // "My tasks", in the menu and in the heading alike — the page answers one question about one
  // person, and a title that said only "Tasks" would read as a project's register.
  "mine.title": "My tasks",
  "mine.title.everyone": "The studio\u2019s work",
  "mine.title.people":
    "{count, plural, one {One person\u2019s work} other {# people\u2019s work}}",
  // The panel field that decides whose work the page is about. Only an admin or a manager is
  // shown it; for everybody else the page is theirs and the question does not arise.
  "mine.who": "Whose work",
  "mine.who.add": "Add a person",
  "mine.who.everyone": "Everyone in the studio",
  "mine.who.everyoneAction": "Show the whole studio",
  "mine.who.mineAgain": "Back to my work",
  "mine.loading": "Loading your tasks…",
  // One sentence each, and the difference is load-bearing: the default question with no answer
  // is good news; a narrowed one with no answer is a statement about the filter.
  "mine.explains": "A task says what needs doing and who does it. Yours show up here.",
  "mine.empty.title": "Nothing waiting",
  "mine.empty": "Nothing is waiting on you.",
  // The way out of an empty front door (owner, 2026-09-17): the projects list is where a person
  // finds what they are on.
  "mine.empty.action": "Go to projects",
  "mine.emptyFiltered.title": "No matches",
  "mine.emptyFiltered": "Nothing you owe matches these filters.",
  // The list's column heads. "Raised" is the row's own date: it is what the page is ordered by.
  "mine.column.task": "Task",
  "mine.column.who": "Who",
  "mine.column.project": "Project",
  "mine.column.status": "Status",
  "mine.column.due": "Due",
  "mine.column.raised": "Raised",
  // A project the caller can reach but the loaded project list does not name yet.
  "mine.projectUnavailable": "Project",
} as const;
